/**
 * Evaluaciones (sección 5, la central): tabla GLOBAL de baterías — niño, padre,
 * banda, modo prueba, estado, perfil, fechas y versiones (engine/scoring).
 * Filtros por estado/modo/banda + paginación simple. El nombre del niño/padre se
 * cruza en el cliente con el listado de niños (viven en identity). Solo lectura.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/endpoints.js";
import { useQuery } from "../../hooks/useQuery.js";
import type { EstadoBateria } from "../../api/types.js";
import { Async, EstadoBadge, PerfilBadge, QaTag, fmtFechaCorta } from "./ui.js";
import s from "./admin.module.css";

const ESTADOS: EstadoBateria[] = [
  "creada",
  "mic_check",
  "en_progreso",
  "completada",
  "procesando",
  "procesada",
  "inconcluyente",
  "suspendida",
  "cancelada",
];
const LIMIT = 50;

export function AdminBaterias() {
  const navigate = useNavigate();
  const [estado, setEstado] = useState<EstadoBateria | "">("");
  const [banda, setBanda] = useState<"" | "A" | "B" | "C" | "D">("");
  const [modo, setModo] = useState<"" | "prueba" | "normal">("");
  const [offset, setOffset] = useState(0);

  const q = useQuery(
    () =>
      adminApi.baterias({
        ...(estado ? { estado } : {}),
        ...(banda ? { banda } : {}),
        ...(modo ? { modo } : {}),
        limit: LIMIT,
        offset,
      }),
    [estado, banda, modo, offset],
  );
  const ninos = useQuery(() => adminApi.ninos());

  const childMap = useMemo(() => {
    const m = new Map<string, { nombre: string; padre: string }>();
    for (const n of ninos.data ?? []) m.set(n.id, { nombre: n.nombre, padre: n.padre?.nombre ?? "—" });
    return m;
  }, [ninos.data]);

  function cambiarFiltro(fn: () => void) {
    setOffset(0);
    fn();
  }

  const total = q.data?.total ?? 0;

  return (
    <>
      <div className={s.toolbar}>
        <select
          className={s.select}
          value={estado}
          onChange={(e) => cambiarFiltro(() => setEstado(e.target.value as EstadoBateria | ""))}
        >
          <option value="">Todos los estados</option>
          {ESTADOS.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
        <select
          className={s.select}
          value={modo}
          onChange={(e) => cambiarFiltro(() => setModo(e.target.value as "" | "prueba" | "normal"))}
        >
          <option value="">Todos los modos</option>
          <option value="normal">Normal</option>
          <option value="prueba">Prueba (QA)</option>
        </select>
        <select
          className={s.select}
          value={banda}
          onChange={(e) => cambiarFiltro(() => setBanda(e.target.value as "" | "A" | "B" | "C" | "D"))}
        >
          <option value="">Todas las bandas</option>
          {(["A", "B", "C", "D"] as const).map((b) => (
            <option key={b} value={b}>
              Banda {b}
            </option>
          ))}
        </select>
        <span className={s.count}>{total} baterías</span>
      </div>

      <div className={s.panel}>
        <Async q={q}>
          {(data) => (
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Niño</th>
                    <th>Padre</th>
                    <th>Banda</th>
                    <th>Modo</th>
                    <th>Estado</th>
                    <th>Perfil</th>
                    <th>Creada</th>
                    <th>Procesada</th>
                    <th>Versiones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((b) => {
                    const c = childMap.get(b.childId);
                    return (
                      <tr
                        key={b.id}
                        className={s.rowClickable}
                        onClick={() => navigate(`/admin/evaluaciones/${b.id}`)}
                      >
                        <td>{c?.nombre ?? <span className={s.mono}>{b.childId.slice(0, 8)}</span>}</td>
                        <td className={s.muted}>{c?.padre ?? "—"}</td>
                        <td>{b.banda}</td>
                        <td>{b.modoPrueba ? <QaTag /> : <span className={s.muted}>normal</span>}</td>
                        <td>
                          <EstadoBadge estado={b.estado} />
                        </td>
                        <td>
                          <PerfilBadge profile={b.profile} />
                        </td>
                        <td className={s.muted}>{fmtFechaCorta(b.createdAt)}</td>
                        <td className={s.muted}>{fmtFechaCorta(b.procesadoEn)}</td>
                        <td className={s.mono}>
                          {b.engineVersion ?? "—"} / {b.scoringVersion ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                  {data.items.length === 0 ? (
                    <tr>
                      <td colSpan={9} className={s.empty}>
                        Sin baterías con esos filtros.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </Async>
      </div>

      <div className={s.toolbar} style={{ marginTop: 12, justifyContent: "flex-end" }}>
        <button className={s.btn} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
          ‹ Anteriores
        </button>
        <span className={s.count}>
          {total === 0 ? 0 : offset + 1}–{Math.min(offset + LIMIT, total)} de {total}
        </span>
        <button
          className={s.btn}
          disabled={offset + LIMIT >= total}
          onClick={() => setOffset(offset + LIMIT)}
        >
          Siguientes ›
        </button>
      </div>
    </>
  );
}
